import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "@/api/axiosInstance";
import type { Product } from "./useProducts";

// Order interfaces based on backend models
export interface CustomizationOption {
    id: number;
    name: string;
    value: string;
    price_modifier: number;
}

export interface OrderItem {
    id: number;
    product: Product;
    quantity: number;
    unit_price: number;
    total_price: number;
    material?: number;
    color?: number;
    size?: number;
    custom_text?: string;
    custom_logo?: string;
    custom_pattern?: string;
    customization_options: CustomizationOption[];
    notes?: string;
    customization?: {
        material?: string;
        color?: string;
        size?: string;
        custom_text?: string;
        custom_logo?: string;
        custom_pattern?: string;
        notes?: string;
    };
}

export interface ShippingAddress {
    id?: number;
    full_name: string;
    company?: string;
    address_line_1: string;
    address_line_2?: string;
    city: string;
    state_province: string;
    postal_code: string;
    country: string;
    phone?: string;
}

export interface PaymentMethod {
    type: string;
    last_four?: string;
}

export interface Order {
    id: number;
    order_number: string;
    status: 'pending' | 'confirmed' | 'in_production' | 'quality_check' | 'shipped' | 'delivered' | 'cancelled';
    items: OrderItem[];
    subtotal: number;
    discount_amount?: number;
    tax_amount: number;
    shipping_cost: number;
    total_amount: number;
    shipping_address: ShippingAddress;
    billing_address?: ShippingAddress;
    payment_method?: PaymentMethod;
    payment_status: 'pending' | 'paid' | 'failed' | 'refunded';
    notes?: string;
    estimated_delivery_date?: string;
    tracking_number?: string;
    tracking_url?: string;
    invoice_url?: string;
    created_at: string;
    updated_at: string;
}

export interface OrderListParams {
    page?: number;
    page_size?: number;
    status?: string;
    ordering?: string;
    date_from?: string;
    date_to?: string;
}

export interface OrderListResponse {
    count: number;
    next?: string;
    previous?: string;
    results: Order[];
}

export interface CreateOrderData {
    items: {
        product_id: number;
        quantity: number;
        material_id?: number;
        color_id?: number;
        size_id?: number;
        custom_text?: string;
        custom_logo?: string;
        custom_pattern?: string;
        notes?: string;
    }[];
    shipping_address: Omit<ShippingAddress, 'id'>;
    billing_address?: Omit<ShippingAddress, 'id'>;
    payment_method?: PaymentMethod;
    notes?: string;
}

export interface OrderStatusUpdate {
    status: Order['status'];
    notes?: string;
    tracking_number?: string;
    estimated_delivery_date?: string;
}

// Fetch orders with filtering and pagination
const fetchOrders = async (params: OrderListParams = {}): Promise<OrderListResponse> => {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            searchParams.append(key, value.toString());
        }
    });

    const { data } = await axiosInstance.get(`/orders/?${searchParams.toString()}`);
    return data;
};

// Fetch single order by ID
const fetchOrder = async (id: number): Promise<Order> => {
    const { data } = await axiosInstance.get(`/orders/${id}/`);
    return data;
};

// Create new order
const createOrder = async (orderData: CreateOrderData): Promise<Order> => {
    const { data } = await axiosInstance.post("/orders/", orderData);
    return data;
};

// Update order status (admin only)
const updateOrderStatus = async (id: number, statusData: OrderStatusUpdate): Promise<Order> => {
    const { data } = await axiosInstance.patch(`/orders/${id}/status/`, statusData);
    return data;
};

// Cancel order
const cancelOrder = async (id: number, reason?: string): Promise<Order> => {
    const { data } = await axiosInstance.post(`/orders/${id}/cancel/`, { reason });
    return data;
};

// Get order tracking info
const getOrderTracking = async (orderNumber: string) => {
    const { data } = await axiosInstance.get(`/orders/tracking/${orderNumber}/`);
    return data;
};

// Hook for fetching user's orders
export const useOrders = (params: OrderListParams = {}) => {
    return useQuery({
        queryKey: ["orders", params],
        queryFn: () => fetchOrders(params),
        staleTime: 1 * 60 * 1000, // 1 minute
        gcTime: 5 * 60 * 1000, // 5 minutes
    });
};

// Hook for fetching single order
export const useOrder = (id: number) => {
    return useQuery({
        queryKey: ["order", id],
        queryFn: () => fetchOrder(id),
        enabled: !!id,
        staleTime: 1 * 60 * 1000, // 1 minute
        gcTime: 5 * 60 * 1000, // 5 minutes
    });
};

// Hook for order tracking
export const useOrderTracking = (orderNumber: string) => {
    return useQuery({
        queryKey: ["order-tracking", orderNumber],
        queryFn: () => getOrderTracking(orderNumber),
        enabled: !!orderNumber,
        staleTime: 30 * 1000, // 30 seconds
        gcTime: 2 * 60 * 1000, // 2 minutes
    });
};

// Hook for creating orders
export const useCreateOrder = () => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: createOrder,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["orders"] });
        },
    });
};

// Hook for updating order status (admin)
export const useUpdateOrderStatus = () => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: ({ id, ...statusData }: { id: number } & OrderStatusUpdate) => 
            updateOrderStatus(id, statusData),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ["orders"] });
            queryClient.invalidateQueries({ queryKey: ["order", data.id] });
        },
    });
};

// Hook for cancelling orders
export const useCancelOrder = () => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: ({ id, reason }: { id: number; reason?: string }) => 
            cancelOrder(id, reason),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ["orders"] });
            queryClient.invalidateQueries({ queryKey: ["order", data.id] });
        },
    });
};

// Admin hook for fetching all orders (with admin filters)
export const useAdminOrders = (params: OrderListParams = {}) => {
    return useQuery({
        queryKey: ["admin-orders", params],
        queryFn: () => fetchOrders(params),
        staleTime: 30 * 1000, // 30 seconds
        gcTime: 2 * 60 * 1000, // 2 minutes
    });
};
